import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import type { Author } from "@/mocks/articles";

function rowToAuthor(row: Record<string, unknown>): Author {
  return {
    name: row.name as string,
    slug: row.slug as string,
    role: (row.role as string) || "",
    shortBio: (row.short_bio as string) || "",
    longBio: (row.long_bio as string) || "",
    email: (row.email as string) || "",
    image: (row.avatar_url as string) || (row.image as string) || "",
    articleCount: (row.article_count as number) || 0,
    author_type: (row.author_type as Author["author_type"]) || "autor",
    isActive: row.is_active as boolean,
    socialLinks: (row.social_links as Author["socialLinks"]) || [],
  };
}

function authorToRow(author: Partial<Author>): Record<string, unknown> {
  const row: Record<string, unknown> = {};
  if (author.name !== undefined) row.name = author.name;
  if (author.slug !== undefined) row.slug = author.slug;
  if (author.role !== undefined) row.role = author.role;
  if (author.shortBio !== undefined) row.short_bio = author.shortBio;
  if (author.longBio !== undefined) row.long_bio = author.longBio;
  if (author.email !== undefined) row.email = author.email;
  if (author.image !== undefined) row.avatar_url = author.image;
  if (author.articleCount !== undefined) row.article_count = author.articleCount;
  if (author.author_type !== undefined) row.author_type = author.author_type;
  if (author.isActive !== undefined) row.is_active = author.isActive;
  if (author.socialLinks !== undefined) row.social_links = author.socialLinks;
  return row;
}

export function useAuthorsDB() {
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAuthors = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data, error: dbError } = await supabase
      .from("authors")
      .select("*")
      .order("name");

    if (dbError) {
      setError(dbError.message);
      setAuthors([]);
    } else {
      setAuthors((data ?? []).map((r) => rowToAuthor(r as Record<string, unknown>)));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAuthors();
  }, [fetchAuthors]);

  const createAuthor = useCallback(async (author: Omit<Author, "id">): Promise<{ data: Author | null; error: string | null }> => {
    const row = authorToRow(author);
    const { data, error: dbError } = await supabase
      .from("authors")
      .insert(row)
      .select()
      .maybeSingle();

    if (dbError) return { data: null, error: dbError.message };
    if (!data) return { data: null, error: "Unbekannter Fehler beim Erstellen." };

    const newAuthor = rowToAuthor(data as Record<string, unknown>);
    await fetchAuthors();
    return { data: newAuthor, error: null };
  }, [fetchAuthors]);

  const updateAuthor = useCallback(async (slug: string, updates: Partial<Author>): Promise<{ error: string | null }> => {
    const row = authorToRow(updates);
    const { error: dbError } = await supabase.from("authors").update(row).eq("slug", slug);
    if (dbError) return { error: dbError.message };
    await fetchAuthors();
    return { error: null };
  }, [fetchAuthors]);

  const deleteAuthor = useCallback(async (slug: string): Promise<{ error: string | null }> => {
    const { error: dbError } = await supabase.from("authors").delete().eq("slug", slug);
    if (dbError) return { error: dbError.message };
    await fetchAuthors();
    return { error: null };
  }, [fetchAuthors]);

  return { authors, loading, error, refetch: fetchAuthors, createAuthor, updateAuthor, deleteAuthor };
}
